<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    :id="id"
    class="modal"
    :class="className">
    <div
      role="document"
      class="modal-dialog"
      :class="{
        'modal-full-width': useFullWidth,
        'modal-full-height': useFullHeight,
        'modal-fit-width': fitToWidth,
        'modal-md': size == 'md',
        'modal-60': size == '60',
        'modal-half': size == 'half',
        'modal-lg': size == 'lg',
        'modal-max-height': maxHeight,
      }">
      <transition
        appear
        name="fade">
        <div
          v-if="visible"
          class="modal-content">
          <div
            v-if="showHeader"
            class="modal-header">
            <h5 class="modal-title">
              <slot name="modal-header-icon" />
              {{ title }}
            </h5>
            <button
              v-if="areButtonsVisible || buttonCloseVisible"
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
              @click="cancel">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            class="modal-body"
            :class="{ 'no-scroll': disableScrolling, 'no-padding': noPadding }">
            <slot name="modal-before-text" />
            <p v-if="body !== ''">
              <icon-colored-warning v-show="isDanger" />
              {{ body }}
            </p>
            <slot name="modal-after-text" />

            <form
              v-if="formFields !== null"
              class="form-horizontal from-fields"
              onsubmit="return false;">
              <div class="form-section-content">
                <div
                  v-for="formField in formFieldsInternal"
                  :key="formField.name"
                  class="form-group row">
                  <form-label
                    :is-required="formField.isMandatory"
                    :size="formFieldsInternal.some((x) => x.description != null) ? 3 : 4"
                    >{{ formField.caption }}
                  </form-label>
                  <div class="col-sm-8">
                    <toggle-button
                      v-if="formField.inputType === ModalFormFieldTypes.boolean"
                      :color="colors.success"
                      :value="formField.value"
                      :labels="{
                        checked: capitalize(i18n.global.t('actions.yes')),
                        unchecked: capitalize(i18n.global.t('actions.no')),
                      }"
                      :sync="true"
                      @change="
                        () => {
                          formField.value = !formField.value;
                        }
                      " />
                    <drop-down
                      v-else-if="
                        [ModalFormFieldTypes.singleSelect, ModalFormFieldTypes.multiSelect].includes(
                          formField.inputType,
                        )
                      "
                      :selected-options="formField.value"
                      option-label-property="name"
                      option-track-by-property="id"
                      option-disabled-property="isDisabled"
                      :options="formField.options"
                      :tint-when-selections="false"
                      :show-popup-header="false"
                      :show-select-all="false"
                      :use-multiselect-style="true"
                      :select-all-by-default="false"
                      :is-multiselect="formField.inputType === ModalFormFieldTypes.multiSelect"
                      @selection-changed="(e) => onFieldSelectionChanged(formField, e)" />
                    <tabs-slider
                      v-else-if="formField.inputType === ModalFormFieldTypes.tabSlider"
                      :tabs="formField.options"
                      :selected-tab-id="formField.options[0].id"
                      @selected-tab-changed="(tab) => (formField.value = tab.id)" />
                    <input
                      v-else-if="formField.inputType === ModalFormFieldTypes.file"
                      :id="formField.id"
                      class="form-control"
                      :name="formField.name"
                      :type="formField.inputType"
                      @change="formFieldChanged($event)" />
                    <div v-else-if="formField.inputType === ModalFormFieldTypes.image">
                      <div
                        v-if="formField.value"
                        class="flex-h mb-3">
                        <img
                          style="max-width: 20rem; max-height: 5rem"
                          :src="formField.overridenValue || formField.value"
                          alt="" />
                        <button
                          class="btn btn-sm btn-default ml-4"
                          @click="removeImage(formField)">
                          Remove
                        </button>
                      </div>
                      <input
                        :id="formField.id"
                        :ref="`input-${formField.id}`"
                        class="form-control"
                        :name="formField.name"
                        type="file"
                        @change="formFieldChanged($event)" />
                    </div>
                    <input
                      v-else-if="formField.inputType === ModalFormFieldTypes.password"
                      v-model="formField.value"
                      class="form-control password-input"
                      :name="formField.name"
                      type="password"
                      autocomplete="new-password"
                      @keyup.enter="confirm" />
                    <textarea
                      v-else-if="formField.inputType === ModalFormFieldTypes.textarea"
                      v-model="formField.value"
                      class="form-control"
                      :name="formField.name"
                      :type="formField.inputType"
                      @keyup.enter="confirm" />
                    <input
                      v-else-if="formField.inputType === ModalFormFieldTypes.number"
                      class="form-control"
                      :name="formField.name"
                      type="number"
                      :value="formField.value"
                      @change="(val) => updateNumber(formField, val)"
                      @keyup.enter="confirm" />
                    <input
                      v-else
                      v-model="formField.value"
                      class="form-control"
                      :name="formField.name"
                      :type="formField.inputType"
                      @keyup.enter="confirm" />
                  </div>
                  <div
                    v-if="formField.description"
                    class="col-sm-1 field-help">
                    <div v-tooltip.left="formField.description">
                      <icon-info />
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <slot name="modal-after-form" />
          </div>
          <div
            v-if="areButtonsVisible"
            class="modal-footer">
            <button
              v-show="button1Visible"
              type="button"
              :class="`btn btn-${button1Class}`"
              name="btnConfirm"
              :disabled="button1Disabled"
              @click="button1Clicked">
              {{ button1Caption }}
            </button>
            <button
              v-show="button2Visible"
              type="button"
              :class="`btn btn-${button2Class}`"
              data-dismiss="modal"
              name="btnCancel"
              :disabled="button2Disabled"
              @click="button2Clicked">
              {{ button2Caption }}
            </button>
            <button
              v-show="button3Visible"
              type="button"
              :class="`btn btn-${button3Class}`"
              data-dismiss="modal"
              name="btnThree"
              :disabled="button3Disabled"
              @click="button3Clicked">
              {{ button3Caption }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script type="text/babel">
  import { capitalize, defineAsyncComponent } from 'vue';

  import colors from '@/Colors';
  import { FormLabel, TabsSlider } from '@/common/components/controls';
  import ModalFormFieldTypes from '@/common/components/controls/modal/ModalFormFieldTypes';
  import { IconColoredWarning } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { isNumeric, readFileAsDataURL } from '@/globals';
  import i18n from '@/vueI18n';

  export default {
    components: {
      DropDown: defineAsyncComponent(() => import('@/common/components/controls/dropdown/DropDown')),
      FormLabel,
      IconColoredWarning,
      IconInfo,
      TabsSlider,
    },
    props: {
      id: {
        type: String,
        default: null,
      },
      title: {
        type: String,
        default: '',
      },
      body: {
        type: String,
        default: '',
      },
      visible: {
        type: Boolean,
        default: false,
      },
      button1Caption: {
        type: String,
        default: 'OK',
      },
      button1Class: {
        type: String,
        default: 'primary',
      },
      button1Visible: {
        type: Boolean,
        default: true,
      },
      button1Disabled: {
        type: Boolean,
        default: false,
      },
      button1Callback: {
        type: Function,
        default: null,
      },
      button2Caption: {
        type: String,
        default: 'Cancel',
      },
      button2Class: {
        type: String,
        default: 'default',
      },
      button2Visible: {
        type: Boolean,
        default: false,
      },
      button2Disabled: {
        type: Boolean,
        default: false,
      },
      button2Callback: {
        type: Function,
        default: null,
      },
      button3Caption: {
        type: String,
        default: 'Cancel',
      },
      button3Class: {
        type: String,
        default: 'default',
      },
      button3Visible: {
        type: Boolean,
        default: false,
      },
      button3Disabled: {
        type: Boolean,
        default: false,
      },
      button3Callback: {
        type: Function,
        default: null,
      },
      buttonCloseVisible: {
        type: Boolean,
        default: false,
      },
      isDanger: {
        type: Boolean,
        default: false,
      },
      formFields: {
        type: Array,
        default: null,
      },
      useFullWidth: {
        type: Boolean,
        default: false,
      },
      useFullHeight: {
        type: Boolean,
        default: false,
      },
      fitToWidth: {
        type: Boolean,
        default: false,
      },
      disableScrolling: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: null,
      },
      noPadding: {
        type: Boolean,
        default: false,
      },
      maxHeight: {
        type: Boolean,
        default: false,
      },
      showHeader: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['confirmed', 'cancelled'],
    data() {
      return {
        colors,
        i18n,
        formFieldsInternal: null,
        ModalFormFieldTypes,
      };
    },
    computed: {
      className() {
        if (this.visible) {
          return 'visible';
        }
        return 'fade';
      },
      areButtonsVisible() {
        return this.button1Visible || this.button2Visible || this.button3Visible;
      },
    },
    watch: {
      formFields() {
        this.updateFormFieldsInternal();
      },
      visible(val) {
        if (!val) {
          return;
        }
        setTimeout(() => {
          const fromFieldFirstTextInput = window.document.querySelector(`#${this.id} .foRm-fields input[type=text]`);
          if (fromFieldFirstTextInput != null) {
            fromFieldFirstTextInput.focus();
          }
        }, 100);
      },
    },
    mounted() {
      this.updateFormFieldsInternal();
      document.querySelectorAll('.password-input').forEach((elm) => {
        elm.addEventListener('focus', () => {
          elm.type = 'password';
        });
      });
    },
    methods: {
      capitalize: (s) => capitalize(s),
      confirm() {
        const form = this.formFields !== null ? this.formFieldsInternal : null;
        this.$emit('confirmed', form);
      },
      cancel() {
        const form = this.formFields !== null ? this.formFieldsInternal : null;
        this.$emit('cancelled', form);
      },
      async formFieldChanged(event) {
        const [formField] = this.formFieldsInternal.filter((x) => x.name.localeCompare(event.target.name) === 0);
        if (formField.inputType.localeCompare('file') === 0 || formField.inputType.localeCompare('image') === 0) {
          formField.value = event.target.files;
          formField.overridenValue = await readFileAsDataURL(event.target.files[0]);
        }
      },
      removeImage(formField) {
        formField.value = null;
        this.$refs[`input-${formField.id}`].value = null;
      },
      button1Clicked() {
        if (this.button1Callback != null) {
          this.button1Callback();
        } else {
          this.confirm();
        }
      },
      button2Clicked() {
        if (this.button2Callback != null) {
          this.button2Callback();
        } else {
          this.cancel();
        }
      },
      button3Clicked() {
        if (this.button3Callback != null) {
          this.button3Callback();
        } else {
          this.cancel();
        }
      },
      updateFormFieldsInternal() {
        if (this.formFields !== null) {
          this.formFieldsInternal = this.formFields.map((x) => {
            return {
              ...x,
              value: x.inputType === ModalFormFieldTypes.boolean ? Boolean(x.value) : x.value || null,
            };
          });
        }
      },
      onFieldSelectionChanged(formField, e) {
        formField.value = e.included;
      },
      updateNumber(formField, e) {
        const num = parseFloat(e.target.value);

        formField.value = isNumeric(e.target.value) ? num : null;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  svg {
    position: relative;
    top: 0.3rem;
    width: 2rem;
    height: 2rem;
    margin-right: 0.4rem;
  }
</style>
